/**
 * ReferrerIndexManager - 推荐人索引管理器
 * 
 * 功能：
 * 1. 维护推荐人 → 被推荐人的索引关系
 * 2. 快速查询被推荐用户列表
 * 3. 统计推荐人数
 * 
 * 类似于 BrokerClientIndexManager 的实现
 */

import type { User } from '../types/user.types';
import { logger } from '../utils/logger';
import { storage } from '../utils/storage';

const STORAGE_KEY = 'referrer_index';

export class ReferrerIndexManager {
  // 推荐人UID → 被推荐人UIDs集合
  private referrerUsers: Map<string, Set<string>> = new Map();

  constructor() {
    this.load();
    logger.log('ReferrerIndexManager initialized');
  }

  /**
   * 添加推荐关系
   */
  addReferredUser(referrerUid: string, referredUid: string): void {
    if (!this.referrerUsers.has(referrerUid)) {
      this.referrerUsers.set(referrerUid, new Set());
    }

    this.referrerUsers.get(referrerUid)!.add(referredUid);
    this.save();

    logger.log(`Added referral relationship: ${referrerUid} → ${referredUid}`);
  }

  /**
   * 移除推荐关系（一般不使用，但保留接口）
   */
  removeReferredUser(referrerUid: string, referredUid: string): void {
    const users = this.referrerUsers.get(referrerUid);
    if (users) {
      users.delete(referredUid);
      if (users.size === 0) {
        this.referrerUsers.delete(referrerUid);
      }
      this.save();
      logger.log(`Removed referral relationship: ${referrerUid} → ${referredUid}`);
    }
  }

  /**
   * 获取推荐人的所有被推荐用户UID
   */
  getReferredUserUids(referrerUid: string): string[] {
    const users = this.referrerUsers.get(referrerUid);
    return users ? Array.from(users) : [];
  }

  /**
   * 获取推荐人的所有被推荐用户对象
   */
  getReferredUsers(referrerUid: string, userManager: any): User[] {
    const uids = this.getReferredUserUids(referrerUid);
    return uids
      .map(uid => userManager.getUserByUID(uid))
      .filter(user => user !== null);
  }

  /**
   * 获取推荐人数
   */
  getReferredUserCount(referrerUid: string): number {
    const users = this.referrerUsers.get(referrerUid);
    return users ? users.size : 0;
  }

  /**
   * 检查是否有推荐关系
   */
  hasReferredUsers(referrerUid: string): boolean {
    return this.getReferredUserCount(referrerUid) > 0;
  }

  /**
   * 获取所有推荐人及其被推荐人数量
   */
  getAllReferrerStats(): Map<string, number> {
    const stats = new Map<string, number>();
    this.referrerUsers.forEach((users, referrerUid) => {
      stats.set(referrerUid, users.size);
    });
    return stats;
  }

  /**
   * 保存到 localStorage
   */
  private save(): void {
    const data: Record<string, string[]> = {};
    this.referrerUsers.forEach((users, referrerUid) => {
      data[referrerUid] = Array.from(users);
    });
    storage.set(STORAGE_KEY, data);
  }

  /**
   * 从 localStorage 加载
   */
  private load(): void {
    const data = storage.get<Record<string, string[]>>(STORAGE_KEY, {});
    Object.entries(data).forEach(([referrerUid, uids]) => {
      this.referrerUsers.set(referrerUid, new Set(uids));
    });
    logger.log(`Loaded ${this.referrerUsers.size} referrer relationships`);
  }

  /**
   * 清空所有索引（仅用于测试）
   */
  clearAll(): void {
    this.referrerUsers.clear();
    storage.remove(STORAGE_KEY);
    logger.log('All referrer indexes cleared');
  }
}

